import { useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { InputField } from '@clay-run/glaze/input-field';
import { Modal, ModalActions, ModalBody, ModalHeader } from '@clay-run/glaze/modal';
import { RadioGroupField } from '@clay-run/glaze/radio-group-field';
import { SelectField } from '@clay-run/glaze/select-field';
import { SwitchField } from '@clay-run/glaze/switch-field';
import { Text } from '@clay-run/glaze/text';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { ArrowDown } from '@phosphor-icons/react/dist/icons/ArrowDown';
import { ArrowUp } from '@phosphor-icons/react/dist/icons/ArrowUp';
import { Check } from '@phosphor-icons/react/dist/icons/Check';
import { Trash } from '@phosphor-icons/react/dist/icons/Trash';
import { AUTOFILL_IGNORE_PROPS } from '../../_shared/autofill';
import { LEAD_FORM_QUESTION_LABELS, questionLabel } from '../lead-forms';
import { MetaLeadFormSpecSchema, type LeadFormQuestion, type MetaLeadFormSpec } from '../types';

// Mirrors Meta's "Create form" dialog: the steps on the left, the step's fields in the middle, and a
// phone preview on the right. Saving keeps the form on the request; Meta creates it at publish.

const STEPS = [
  { key: 'type', label: 'Form type' },
  { key: 'intro', label: 'Intro' },
  { key: 'questions', label: 'Questions' },
  { key: 'privacy', label: 'Privacy policy' },
  { key: 'ending', label: 'Ending' },
] as const;
type StepKey = (typeof STEPS)[number]['key'];

const STEP_OF_FIELD: Record<string, StepKey> = {
  name: 'type',
  optimizedForQuality: 'type',
  contextCard: 'intro',
  questionHeadline: 'questions',
  questions: 'questions',
  privacyPolicy: 'privacy',
  thankYou: 'ending',
  locale: 'type',
};

const BUTTON_TEXTS = ['Learn more', 'Visit website', 'View website', 'Register', 'Sign up', 'Download', 'Get started'];

function invalidSteps(spec: MetaLeadFormSpec): Set<StepKey> {
  const result = MetaLeadFormSpecSchema.safeParse(spec);
  if (result.success) return new Set();
  return new Set(result.error.issues.map(i => STEP_OF_FIELD[String(i.path[0])] ?? 'type'));
}

function FormPreview({ spec, page }: { spec: MetaLeadFormSpec; page: 'intro' | 'questions' | 'ending' }) {
  return (
    <div className="flex h-[440px] flex-col overflow-hidden rounded-xl border border-border-primary bg-bg-secondary">
      <div className="flex flex-1 flex-col justify-center p-4">
        <div className="flex flex-col gap-3 rounded-lg bg-bg-primary p-4 text-sm shadow-sm">
          {page === 'intro' && spec.contextCard && (
            <>
              <span className="text-center text-xs text-content-tertiary">Clay GTM</span>
              <span className="text-center font-semibold">{spec.contextCard.title || 'Headline text'}</span>
              {spec.contextCard.content.map(line => (
                <span key={line} className="text-xs">
                  {spec.contextCard?.style === 'LIST_STYLE' ? `• ${line}` : line}
                </span>
              ))}
            </>
          )}
          {page === 'questions' && (
            <>
              <span className="font-medium">Contact information</span>
              {spec.questionHeadline && <span className="text-xs">{spec.questionHeadline}</span>}
              {spec.questions.map(q => (
                <div key={`${q.type}-${q.key ?? q.label ?? ''}`} className="flex flex-col gap-0.5 border-b border-border-primary pb-1.5">
                  <span className="text-xs text-content-tertiary">{questionLabel(q)}</span>
                  <span className="text-xs text-content-tertiary">Enter your answer.</span>
                </div>
              ))}
            </>
          )}
          {page === 'ending' && (
            <>
              <span className="text-center text-xs text-content-tertiary">Clay GTM</span>
              <span className="text-center font-semibold">{spec.thankYou.title}</span>
              <span className="text-xs">{spec.thankYou.body}</span>
              <span className="border-t border-border-primary pt-2 text-center text-[11px] text-content-tertiary">
                You successfully submitted your responses.
              </span>
            </>
          )}
        </div>
      </div>
      <div className="bg-bg-primary p-3">
        <div className="rounded-md bg-bg-action-inverse py-2 text-center text-sm font-medium text-content-inverse">
          {page === 'intro' ? spec.contextCard?.buttonText || 'Continue' : page === 'questions' ? 'Continue' : spec.thankYou.buttonText || 'Done'}
        </div>
      </div>
    </div>
  );
}

function QuestionRow({
  question,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  question: LeadFormQuestion;
  first: boolean;
  last: boolean;
  onChange: (q: LeadFormQuestion) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const custom = !(question.type in LEAD_FORM_QUESTION_LABELS);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border-primary p-2.5">
      <div className="flex flex-col">
        <Button size="sm" variant="ghost" aria-label="Move up" disabled={first} onClick={() => onMove(-1)}>
          <ArrowUp size={12} />
        </Button>
        <Button size="sm" variant="ghost" aria-label="Move down" disabled={last} onClick={() => onMove(1)}>
          <ArrowDown size={12} />
        </Button>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {custom ? (
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            aria-label="Question"
            value={question.label ?? ''}
            onValueChange={label => onChange({ ...question, label })}
          />
        ) : (
          <span className="text-sm">{questionLabel(question)}</span>
        )}
        {custom && (
          <Text size="xs" variant="tertiary">
            Custom question · {question.type.toLowerCase().replaceAll('_', ' ')}
          </Text>
        )}
      </div>
      {question.type === 'WORK_EMAIL' && (
        <Text size="xs" variant="tertiary" className="max-w-40">
          Meta's API can't turn on "Require validation"; set it in Ads Manager if needed.
        </Text>
      )}
      <Button size="sm" variant="ghost" aria-label={`Remove ${questionLabel(question)}`} onClick={onRemove}>
        <Trash size={14} />
      </Button>
    </div>
  );
}

function StepFields({ step, spec, onChange }: { step: StepKey; spec: MetaLeadFormSpec; onChange: (s: MetaLeadFormSpec) => void }) {
  const thankYou = (fields: Partial<MetaLeadFormSpec['thankYou']>) => onChange({ ...spec, thankYou: { ...spec.thankYou, ...fields } });
  const setQuestions = (questions: LeadFormQuestion[]) => onChange({ ...spec, questions });
  const addable = Object.keys(LEAD_FORM_QUESTION_LABELS).filter(type => !spec.questions.some(q => q.type === type));

  switch (step) {
    case 'type':
      return (
        <div className="flex flex-col gap-4">
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Form name"
            description="Only seen in Ads Manager."
            value={spec.name}
            onValueChange={name => onChange({ ...spec, name })}
          />
          <RadioGroupField
            label="Form type"
            value={spec.optimizedForQuality ? 'quality' : 'volume'}
            onValueChange={v => onChange({ ...spec, optimizedForQuality: v === 'quality' })}
            options={[
              { value: 'volume', label: 'More volume', description: "A form that's quick to fill out and submit on a mobile device." },
              { value: 'quality', label: 'Higher intent', description: 'Adds a review step so people confirm their info.' },
            ]}
          />
          <div className="flex flex-col gap-1">
            <Text size="sm" weight="medium">
              Flexible form delivery
            </Text>
            <Text size="xs" variant="tertiary">
              Optimized: Meta tailors the form to each person. Meta's default, which this app keeps.
            </Text>
          </div>
        </div>
      );
    case 'intro':
      return (
        <div className="flex flex-col gap-4">
          <SwitchField
            label="Background image"
            description="Not supported here yet; set it in Ads Manager if needed."
            checked={false}
            disabled
          />
          <SwitchField
            label="Greeting"
            description="Let people know any relevant details and why they should complete your form."
            checked={spec.contextCard !== null}
            onCheckedChange={on =>
              onChange({
                ...spec,
                contextCard: on ? { title: '', style: 'PARAGRAPH_STYLE', content: [''], buttonText: 'Continue' } : null,
              })
            }
          />
          {spec.contextCard && (
            <>
              <InputField
                {...AUTOFILL_IGNORE_PROPS}
                label="Headline"
                value={spec.contextCard.title}
                onValueChange={title => spec.contextCard && onChange({ ...spec, contextCard: { ...spec.contextCard, title } })}
              />
              <RadioGroupField
                label="Layout"
                value={spec.contextCard.style}
                onValueChange={style => spec.contextCard && onChange({ ...spec, contextCard: { ...spec.contextCard, style } })}
                options={[
                  { value: 'PARAGRAPH_STYLE', label: 'Paragraph' },
                  { value: 'LIST_STYLE', label: 'List' },
                ]}
              />
              <TextareaField
                label={spec.contextCard.style === 'LIST_STYLE' ? 'Items, one per line' : 'Paragraph'}
                rows={3}
                value={spec.contextCard.content.join('\n')}
                onValueChange={text =>
                  spec.contextCard &&
                  onChange({ ...spec, contextCard: { ...spec.contextCard, content: text.split('\n').slice(0, 5) } })
                }
              />
              <InputField
                {...AUTOFILL_IGNORE_PROPS}
                label="Button text"
                value={spec.contextCard.buttonText}
                onValueChange={buttonText => spec.contextCard && onChange({ ...spec, contextCard: { ...spec.contextCard, buttonText } })}
              />
            </>
          )}
        </div>
      );
    case 'questions':
      return (
        <div className="flex flex-col gap-4">
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Description"
            description="Shown above the questions under Contact information. Let people know what they're signing up for."
            value={spec.questionHeadline}
            onValueChange={questionHeadline => onChange({ ...spec, questionHeadline })}
          />
          <div className="flex flex-col gap-2">
            {spec.questions.map((q, i) => (
              <QuestionRow
                key={`${q.type}-${q.key ?? i}`}
                question={q}
                first={i === 0}
                last={i === spec.questions.length - 1}
                onChange={next => setQuestions(spec.questions.map((x, j) => (j === i ? next : x)))}
                onMove={by => {
                  const next = [...spec.questions];
                  const [moved] = next.splice(i, 1);
                  if (moved) next.splice(i + by, 0, moved);
                  setQuestions(next);
                }}
                onRemove={() => setQuestions(spec.questions.filter((_, j) => j !== i))}
              />
            ))}
          </div>
          {addable.length > 0 && (
            <SelectField
              label="Add question"
              placeholder="Pick a question"
              items={addable.map(type => ({ value: type, label: LEAD_FORM_QUESTION_LABELS[type] ?? type }))}
              value={undefined}
              onValueChange={type => typeof type === 'string' && setQuestions([...spec.questions, { type }])}
            />
          )}
        </div>
      );
    case 'privacy':
      return (
        <div className="flex flex-col gap-4">
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Link"
            type="url"
            value={spec.privacyPolicy.url}
            onValueChange={url => onChange({ ...spec, privacyPolicy: { ...spec.privacyPolicy, url } })}
          />
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Link text"
            description={`${spec.privacyPolicy.linkText.length} / 70`}
            value={spec.privacyPolicy.linkText}
            onValueChange={linkText => onChange({ ...spec, privacyPolicy: { ...spec.privacyPolicy, linkText } })}
          />
        </div>
      );
    case 'ending':
      return (
        <div className="flex flex-col gap-4">
          <InputField
            {...AUTOFILL_IGNORE_PROPS}
            label="Headline"
            description={`${spec.thankYou.title.length} / 60`}
            value={spec.thankYou.title}
            onValueChange={title => thankYou({ title })}
          />
          <TextareaField label="Description" rows={3} value={spec.thankYou.body} onValueChange={body => thankYou({ body })} />
          <RadioGroupField
            label="Additional action"
            value={spec.thankYou.buttonType === 'VIEW_WEBSITE' ? 'VIEW_WEBSITE' : 'NONE'}
            onValueChange={v =>
              thankYou(
                v === 'VIEW_WEBSITE'
                  ? { buttonType: 'VIEW_WEBSITE', websiteUrl: spec.thankYou.websiteUrl ?? 'https://www.clay.com/' }
                  : { buttonType: 'NONE', websiteUrl: null },
              )
            }
            options={[
              { value: 'VIEW_WEBSITE', label: 'Go to website', description: 'Share a URL for people to visit your website.' },
              { value: 'NONE', label: 'None' },
            ]}
          />
          {spec.thankYou.buttonType === 'VIEW_WEBSITE' && (
            <>
              <InputField
                {...AUTOFILL_IGNORE_PROPS}
                label="Link"
                type="url"
                value={spec.thankYou.websiteUrl ?? ''}
                onValueChange={websiteUrl => thankYou({ websiteUrl })}
              />
              <SelectField
                label="Call to action"
                items={[...new Set([spec.thankYou.buttonText, ...BUTTON_TEXTS])].filter(Boolean).map(t => ({ value: t, label: t }))}
                value={spec.thankYou.buttonText}
                onValueChange={buttonText => thankYou({ buttonText: String(buttonText) })}
              />
            </>
          )}
        </div>
      );
  }
}

/** Mount with a `key` per opening, so it starts from the spec it's given. */
export function LeadFormEditor({
  initial,
  onSave,
  onClose,
}: {
  initial: MetaLeadFormSpec;
  onSave: (spec: MetaLeadFormSpec) => void;
  onClose: () => void;
}) {
  const [spec, setSpec] = useState(initial);
  const [stepIndex, setStepIndex] = useState(0);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [platform, setPlatform] = useState<'facebook' | 'instagram'>('facebook');
  const step = STEPS[stepIndex]?.key ?? 'type';
  const invalid = invalidSteps(spec);
  const pages = [...(spec.contextCard ? (['intro'] as const) : []), 'questions', 'ending'] as const;
  const page = pages[Math.min(previewIndex, pages.length - 1)] ?? 'questions';
  const pageLabel = { intro: 'Intro', questions: 'Prefill information', ending: 'Message for leads' }[page];

  return (
    <Modal open size="6xl" onClose={onClose}>
      <ModalHeader onClose={onClose}>Instant form</ModalHeader>
      <ModalBody>
        <div className="grid min-h-[520px] grid-cols-[180px_minmax(0,1fr)_300px] gap-6">
          <nav className="flex flex-col gap-1">
            {STEPS.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setStepIndex(i)}
                className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm ${i === stepIndex ? 'bg-bg-secondary font-medium' : ''}`}
              >
                <span
                  className={`flex size-4 items-center justify-center rounded-full border ${
                    invalid.has(s.key) ? 'border-border-primary' : 'border-transparent bg-bg-action-inverse text-content-inverse'
                  }`}
                >
                  {!invalid.has(s.key) && <Check size={10} />}
                </span>
                {s.label}
              </button>
            ))}
          </nav>
          <div className="min-w-0 overflow-y-auto pr-1">
            <StepFields step={step} spec={spec} onChange={setSpec} />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-1 self-center rounded-lg bg-bg-secondary p-0.5 text-xs">
              {(['facebook', 'instagram'] as const).map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatform(p)}
                  className={`rounded-md px-3 py-1 ${platform === p ? 'bg-bg-primary font-medium' : 'text-content-tertiary'}`}
                >
                  {p === 'facebook' ? 'Facebook' : 'Instagram'}
                </button>
              ))}
            </div>
            <FormPreview spec={spec} page={page} />
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">{pageLabel}</span>
              <span className="flex items-center gap-1">
                <Button size="sm" variant="secondary" disabled={previewIndex === 0} onClick={() => setPreviewIndex(i => i - 1)}>
                  ‹
                </Button>
                {Math.min(previewIndex, pages.length - 1) + 1} of {pages.length}
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={previewIndex >= pages.length - 1}
                  onClick={() => setPreviewIndex(i => i + 1)}
                >
                  ›
                </Button>
              </span>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Text size="xs" variant="tertiary" className="mr-auto">
          Saved to this request. Meta creates the form when the request is published.
        </Text>
        <Button variant="secondary" disabled={stepIndex === 0} onClick={() => setStepIndex(i => i - 1)}>
          Previous
        </Button>
        {stepIndex < STEPS.length - 1 ? (
          <Button variant="secondary" onClick={() => setStepIndex(i => i + 1)}>
            Next
          </Button>
        ) : null}
        <Button variant="primary" disabled={invalid.size > 0} onClick={() => onSave(spec)}>
          Save form
        </Button>
      </ModalActions>
    </Modal>
  );
}
